/**
 * Calendar/Content Pool 的真实数据源(2026-08-24,替换 MOCK_CALENDAR_ITEMS)。
 *
 * 三来源汇流(2026-08-24 起全部就位,存储源 feature-detect):
 *   - listContentDrafts  → 未发布的草稿 = Content Pool(date=null):Maya 每次
 *     CREATE_CONTENT_DRAFT 都落这张表,这就是"池子为什么该有东西"的那份数据;
 *   - listPublishedContent → 已发布/发布失败的条目 = 日历上的既成事实
 *     (按 draftId 归并多渠道成一条多版本)。
 *   - fetchCalendarStoreMonth → yepairag 日历存储(PR #1206)的排期行 + 池:
 *     批量计划的每一行、单篇归档、发布翻转都在这。后端未部署时该请求失败 →
 *     自动回落两源视图,部署后无需改前端。
 * 三源重叠的去重键 = agent draft_id(存储 versions.draftId ↔ 草稿 clientDraftId
 * ↔ 发布记录经 draftId→草稿 id 映射),存储行优先(它有排期语义)。
 */
import { useCallback, useEffect, useState } from 'react';
import {
  fetchCalendarStoreMonth,
  type StoreCalendarItem,
} from '@lib/services/agents/calendarScheduleApi';
import { useAuthStore } from '@stores/authStore';
import {
  listContentDrafts,
  type ContentDraft,
  DRAFT_LIST_TIMEOUT_MS,
} from '@lib/services/agents/contentDraftApi';
import {
  listPublishedContent,
  type PublishedContent,
  PUBLISHED_LIST_TIMEOUT_MS,
} from '@lib/services/agents/publishedContentApi';
import type { CalendarItem, PlatformKey, PlatformVersion } from './calendarContent';
import { wallClockIn } from '@lib/utils/wallClock';

/**
 * [3.5 #9] 也给聊天里的批量完成卡用 —— 它要按 plan_id 从日历行里认出失败的那几篇,
 * 而存储行的 platform 是自由字符串。归一口径只留这一份,别再写第二个。
 */
export function platformKeyOf(
  raw: string | null | undefined,
  contentType?: string | null
): PlatformKey {
  const low = (raw ?? '').toLowerCase();
  if (low.includes('instagram')) return 'instagram';
  if (low.includes('facebook')) return 'facebook';
  if (low.includes('shopify') || low.includes('blog')) return 'shopify_blog';
  return (contentType ?? '').toLowerCase() === 'blog' ? 'shopify_blog' : 'instagram';
}

function parseJson<T>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function productCount(snapshot: string | null | undefined): number {
  const parsed = parseJson<unknown>(snapshot);
  return Array.isArray(parsed) ? parsed.length : 0;
}

function campaignOf(snapshot: string | null | undefined): CalendarItem['campaign'] {
  const parsed = parseJson<Record<string, unknown>>(snapshot);
  const code =
    typeof parsed?.code === 'string'
      ? parsed.code
      : typeof parsed?.discount_code === 'string'
        ? parsed.discount_code
        : null;
  if (!code) return undefined;
  const label = typeof parsed?.label === 'string' ? parsed.label : code;
  return { code, label };
}

function draftVersion(d: ContentDraft): PlatformVersion {
  const platform = platformKeyOf(d.platform, d.contentType);
  return {
    platform,
    // [CUS-29] 这一篇的身份。缺了它,从内容池点 Publish Now 发的就是一篇「无名氏」:
    // publishNowJob 读 v.draftId 传给直发接口,拿到 undefined → /item/published 报
    // draft_id=null → 后端 mark_published 既按 draft_id 认不出、又没有对应的存储行可
    // 按标题认,于是走「兜底补建」新开一行(无 draft_id、无时间);而 chatbot-api 那边
    // 归档的发布记录**是**带着 agent draft id 的,两边对不上号 → mergeCalendarSources
    // 去重失败 → 同一篇在「今天」画两张卡。实测 2026-08-31 pomef 租户:连发两篇,
    // 今天那格出现 4 张卡(两张带时间、两张不带),测试读成「发一个把另一个也发了」。
    // 用 clientDraftId(agent 形态 draft_<hex>)——存储行的 versions.draft_id 和发布
    // 记录的 draftId 用的都是这一种,行 UUID 在这里谁也认不出。
    ...(d.clientDraftId ? { draftId: d.clientDraftId } : {}),
    title: d.title ?? 'Untitled draft',
    body: d.body ?? '',
    imageUrl: d.imageUrl ?? undefined,
    ...(platform === 'instagram' ? { charLimit: 2200 } : {}),
    status: 'ok',
  };
}

/** 纯映射,单测入口:chatbot-api 的草稿+发布记录 → 日历条目。 */
export function mapToCalendarItems(
  drafts: ContentDraft[],
  published: PublishedContent[]
): CalendarItem[] {
  // [3.5 #2 兜底] 发布记录(PublishedContent)不带任何图片字段 —— 聊天直发的已发布帖在日历上
  // 因此**完全没图**。这里按 draft_id 借回对应草稿的封面(agent draft_<hex> 与行 UUID 两种键都建),
  // 让这些帖子在日历上至少显示它自己的图。
  // ⚠ 这只补「没图」:草稿在发布后又被改过图时,借到的是新草稿图、未必是当时真正发出去的那张
  //   —— 那属于「图不对」,根因是发布未回写实际用图 URL(序号10),要后端修,前端借不出来。
  const imageByDraftId = new Map<string, string>();
  for (const d of drafts) {
    if (!d.imageUrl) continue;
    if (d.clientDraftId && !imageByDraftId.has(d.clientDraftId)) {
      imageByDraftId.set(d.clientDraftId, d.imageUrl);
    }
    if (!imageByDraftId.has(d.id)) imageByDraftId.set(d.id, d.imageUrl);
  }
  // 已发布:按 draftId 归并(一稿多渠道 = 一条多版本);没有 draftId 的各自成条。
  const groups = new Map<string, PublishedContent[]>();
  for (const p of published) {
    const key = p.draftId || `pub-${p.id}`;
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  }
  const publishedItems: CalendarItem[] = [...groups.entries()].map(([key, list]) => {
    const first = list[0];
    const when = first.publishedAt ?? first.createdAt ?? null;
    // [3.5 #11] 以前这里是 `when.slice(0,10)` / `when.slice(11,16)` —— 直接从 ISO 串上
    // 切片,切出来的是 **UTC**。所以一篇 01:21Z 发出去的帖子,在悉尼的商家眼里也写着
    // 01:21,而他当地其实是 11:21。已发布时刻是个绝对时刻,要按商家浏览器时区读。
    const at = when ? new Date(when) : null;
    const wall = at && !Number.isNaN(at.getTime()) ? wallClockIn(at, null) : null;
    const date = wall?.date ?? null;
    const time = wall?.time ?? '';
    const versions = list.map<PlatformVersion>(p => ({
      platform: platformKeyOf(p.channel),
      // [CUS-29] 同 draftVersion:身份要带上,否则这条已发布条目对任何按 draft id
      // 认稿的下游(重发、指标回填)都是无名氏。
      ...(p.draftId ? { draftId: p.draftId } : {}),
      // 原帖地址 —— 详情弹窗的「View post」靠它。发布记录是链路上唯一有这个值的源。
      ...(p.externalUrl ? { externalUrl: p.externalUrl } : {}),
      // [3.5 #2 兜底] 借草稿封面补图(发布记录本身无图字段)。
      ...(p.draftId && imageByDraftId.get(p.draftId)
        ? { imageUrl: imageByDraftId.get(p.draftId) as string }
        : {}),
      title: p.title ?? 'Untitled',
      body: p.body ?? '',
      status: p.status === 'FAILED' ? 'failed' : 'ok',
      ...(p.status === 'FAILED' && p.failureReason ? { failReason: p.failureReason } : {}),
    }));
    const anyPublished = list.some(p => p.status === 'PUBLISHED');
    return {
      id: `pub-${key}`,
      title: first.title ?? 'Untitled',
      date,
      time,
      genCount: 1,
      status: anyPublished ? 'published' : 'scheduled',
      setup: { label: 'Published from Maya chat', products: 0, keywords: 0 },
      versions,
      // 已发布的条目有日期,进的是日历不是池子;带上时间戳只为「两者都缺」的边缘
      // 情况(date 落成 null → 它会掉进池子)也能参与排序,而不是排到末尾。
      ...(when ? { contentAt: when } : {}),
      ...(first.createdAt ? { createdAt: first.createdAt } : {}),
    } as CalendarItem;
  });

  // 池子:还没发布也没丢弃的草稿。已发布的草稿由上面的发布条目代表,不重复进池。
  // 发布记录的 draftId 两种形态都有(行 UUID 或 agent 的 draft_<hex>,见 mergeCalendarSources),
  // 所以草稿的两个 id 任一出现在发布记录里都算已发布。原来只看行 id,用 agent id 发出去的稿子
  // 还留在 unscheduled 里(与 yepairag GET_CONTENT_CALENDAR 的 total_pool 口径不一致)。
  const publishedDraftIds = new Set(published.map(p => p.draftId).filter(Boolean));
  const isPublished = (d: ContentDraft) =>
    publishedDraftIds.has(d.id) || (!!d.clientDraftId && publishedDraftIds.has(d.clientDraftId));
  const poolItems: CalendarItem[] = drafts
    .filter(d => (d.status === 'DRAFT' || d.status === 'APPROVED') && !isPublished(d))
    .map(
      d =>
        ({
          id: `draft-${d.id}`,
          title: d.title ?? 'Untitled draft',
          date: null,
          time: '',
          genCount: 1,
          status: 'scheduled',
          setup: {
            label: 'From Maya chat',
            products: productCount(d.productsSnapshot),
            keywords: 0,
          },
          campaign: campaignOf(d.campaignSnapshot),
          versions: [draftVersion(d)],
          // 草稿的两个时刻是同一件事(写出来就落库),没有"先排计划"那一步。
          // 没有它们的话,这一整段(chatbot-api 源)只能整块拼在存储源后面 ——
          // 接缝处时间直接回跳几天,这正是 2026-08-30 之前的样子。
          ...(d.createdAt ? { contentAt: d.createdAt, createdAt: d.createdAt } : {}),
        }) as CalendarItem
    );

  return [...poolItems, ...publishedItems];
}

/**
 * One store row → one calendar entry.
 *
 * Exported because the Task Centre panel opens the Hub's editor over a single
 * piece read by id, and it has to be the SAME mapping the calendar uses — a
 * second copy would drift and the panel would quietly show a different post
 * from the one the calendar shows.
 */
export function storeItemToCalendarItem(it: StoreCalendarItem): CalendarItem {
  // [2026-09-10] 库里的 date/time 就是**商家自己那块表**的墙上时钟 —— 他选的时候没换算,
  // 发布器解释的时候也用他的时区(allTenantZones)。所以这里原样显示,不再换算。
  return {
    id: it.id,
    // 批次身份只有存储源有(chatbot-api 的草稿/发布两源是聊天里的单篇,天然无批次)。
    ...(it.planId ? { planId: it.planId } : {}),
    ...(it.planLabel ? { planLabel: it.planLabel } : {}),
    title: it.title,
    date: it.date,
    // 池子里的行本来就没有时刻,别补出个 '00:00'。
    time: it.time ?? '',
    genCount: it.genCount ?? 0,
    status: it.status === 'published' ? 'published' : 'scheduled',
    setup: it.setup ?? { label: 'Planned', products: 0, keywords: 0 },
    campaign: it.campaign ?? undefined,
    ...(it.contentAt ? { contentAt: it.contentAt } : {}),
    ...(it.createdAt ? { createdAt: it.createdAt } : {}),
    ...(typeof it.planSeq === 'number' ? { planSeq: it.planSeq } : {}),
    versions: (it.versions ?? []).map<PlatformVersion>(v => ({
      platform: platformKeyOf(v.platform),
      title: v.title ?? '',
      body: v.body ?? '',
      imageUrl: v.imageUrl ?? undefined,
      status: v.status === 'failed' ? 'failed' : 'ok',
      ...(v.failReason ? { failReason: v.failReason } : {}),
      // 'publish' 以外的一切(含缺省)都按生成失败处理:Retry 跳 Chat 重跑 writer,
      // 那是"内容可能不存在"时唯一安全的动作 —— 误判成发布失败会去发一篇空稿。
      ...(v.failStage === 'publish' ? { failStage: 'publish' as const } : {}),
      ...(v.draftId ? { draftId: v.draftId } : {}),
      // [3.5 #3] 原帖地址:两种拼法都收(后端命名未最终对齐),都没有就留空 ——
      // 只读态会渲染一个置灰的 View post 并说明原因,不是什么都不放。
      ...(v.externalUrl || v.external_url
        ? { externalUrl: (v.externalUrl ?? v.external_url) as string }
        : {}),
    })),
  } as CalendarItem;
}

/** 存储行里「没日期、但已发布 / 已丢弃」的 —— 不是待排内容。 */
function isSettledUndatedRow(it: StoreCalendarItem): boolean {
  if (it.date) return false;
  const status = (it.status ?? '').toLowerCase();
  return status === 'published' || status === 'discarded';
}

/** 三源合并(纯函数,单测入口):存储行优先,chatbot-api 草稿/发布记录按 draft_id 去重后补位。 */
export function mergeCalendarSources(
  store: {
    items: StoreCalendarItem[];
    pool: StoreCalendarItem[];
    /** 别的月份已排期的草稿 id —— 见下面的用处。老后端不发这个字段。 */
    scheduledDraftIds?: string[];
    /** 存储源认领过的全部草稿 id(含被改稿顶掉的旧版)。老后端不发这个字段。 */
    knownDraftIds?: string[];
  } | null,
  drafts: ContentDraft[],
  published: PublishedContent[]
): CalendarItem[] {
  // 纵深防御：存储源返回畸形负载（如伪 200 的错误信封）时按「没有存储源」处理，
  // 绝不能让它把成功的草稿/发布两源一起打空。
  if (!store || !Array.isArray(store.items) || !Array.isArray(store.pool)) {
    return mapToCalendarItems(drafts, published);
  }
  // 没排日期、但已经发布或丢弃的存储行不是待排内容,不进内容池(产品口径 2026-10-27,与
  // yepairag GET_CONTENT_CALENDAR 的 total_pool 一致)。它们的 draft id 仍算「存储源认领过」
  // (下面的 storeDraftIds 用全部行),所以对应的聊天草稿也不会被当散稿补回池子。
  const storeItems = [...store.items, ...store.pool]
    .filter(it => !isSettledUndatedRow(it))
    .map(it => storeItemToCalendarItem(it));
  const storeDraftIds = new Set([
    ...[...store.items, ...store.pool].flatMap(it =>
      (it.versions ?? []).map(v => v.draftId).filter((x): x is string => !!x)
    ),
    // 别的月份的已排期草稿。store.items 只装本月,少了这一份名单,九月排好的
    // 稿子在八月视图里既不在 items 也不在 pool,于是下面把它当成「没有存储源的
    // 散稿」重新并进内容池 —— 商家会把已经排期的帖子再拖一次,Clear pool 还会
    // 误伤它们。
    ...(store.scheduledDraftIds ?? []),
    // 同一个缺口的另一半:被改稿顶掉的旧 draft_id。日历行一个渠道只留最新那版,
    // 而 chatbot-api 的草稿一版一行、旧行永远 DRAFT —— 于是每改一次稿,池子里就
    // 多一张假卡(点得开、拖得动,但排期接口只收 cal_ 行,松手后什么都没发生;
    // Clear pool 反而真把它删掉)。dev 2026-08-30 实测一个租户 147 张。
    // 两个字段都并进来:knownDraftIds 是 scheduledDraftIds 的超集,但后端可能
    // 只发出其中一个(旧镜像没有新字段),取并集才不会因为部署顺序而回退。
    ...(store.knownDraftIds ?? []),
  ]);
  // chatbot-api 草稿的 agent id = clientDraftId。发布记录的 draftId 两种形态都可能:
  // agent 的 draft_<hex>,或 chatbot-api 自己的行 UUID(resolveDraftId 两者都收)。
  // 只映射 UUID→agent id 会让带 agent id 的记录去重失败 —— 同一篇内容在日历上出现两次
  // (存储源一条 + 发布源一条)。两个方向都建映射,再拿记录自己的 draftId 兜底。
  const toAgentId = new Map<string, string>();
  for (const d of drafts) {
    if (!d.clientDraftId) continue;
    toAgentId.set(d.id, d.clientDraftId);
    toAgentId.set(d.clientDraftId, d.clientDraftId);
  }
  const restDrafts = drafts.filter(d => !(d.clientDraftId && storeDraftIds.has(d.clientDraftId)));
  const restPublished = published.filter(p => {
    const agentId = p.draftId ? (toAgentId.get(p.draftId) ?? p.draftId) : undefined;
    return !(agentId && storeDraftIds.has(agentId));
  });
  // 被存储行去重掉的那些发布记录里有一样东西是存储行没有的:原帖地址。
  // yepairag 的 /calendar/month 不返回 external_url(写得进去、读不回来),于是
  // 排期发出去的帖子在日历上永远没有「View post」,而聊天直发的(pub- 行)有 ——
  // 同一个功能对商家时灵时不灵。这里按 draft id 把它借回存储行的对应版本上。
  const urlByDraftId = new Map<string, string>();
  for (const p of published) {
    if (!p.draftId || !p.externalUrl) continue;
    const agentId = toAgentId.get(p.draftId) ?? p.draftId;
    if (!urlByDraftId.has(agentId)) urlByDraftId.set(agentId, p.externalUrl);
  }
  const storeItemsWithUrls = urlByDraftId.size
    ? storeItems.map(it => ({
        ...it,
        versions: it.versions.map(v => {
          const url = v.draftId ? urlByDraftId.get(v.draftId) : undefined;
          return url && !v.externalUrl ? { ...v, externalUrl: url } : v;
        }),
      }))
    : storeItems;
  return [...storeItemsWithUrls, ...mapToCalendarItems(restDrafts, restPublished)];
}

export function useCalendarContent(month: string): {
  items: CalendarItem[];
  loading: boolean;
  error: boolean;
  refetch: () => void;
} {
  const user = useAuthStore(s => s.user);
  const userId = user?.id;
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  // [3.5 #11] 只用来把库里的店铺墙上时钟换成商家的浏览器时钟 —— 不再用它算「今天」。

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    Promise.all([
      // Both lists were read with the client's 10 s default and, on dev under load, timed
      // out (2026-09-08) — and a failed drafts read hides every pool item, because the pool
      // is filtered by the draft ids it knows. 60 s: the rows arrive, the pool draws.
      listContentDrafts(userId, { timeoutMs: DRAFT_LIST_TIMEOUT_MS }),
      listPublishedContent(userId, { timeoutMs: PUBLISHED_LIST_TIMEOUT_MS }),
      // 存储源单独兜底:后端未部署(404/网络)不拖垮另两源。
      fetchCalendarStoreMonth(userId, month).catch(() => null),
    ])
      .then(([drafts, published, store]) => {
        if (!cancelled) setItems(mergeCalendarSources(store, drafts, published));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, month, reload]);

  const refetch = useCallback(() => setReload(n => n + 1), []);
  return { items, loading, error, refetch };
}
